<template>
  <div class="mb-15">
    <div class="d-flex align-center">
      <page-title value="成長記録一覧" />
      <v-spacer />
      <div
        style="width: 160px;"
        class="mx-2"
      >
        <v-text-field
          hide-details
          density="compact"
          variant="outlined"
          type="number"
          v-model.number="year"
          suffix="年度"
          color="primary"
        ></v-text-field>
      </div>
      <v-checkbox
        v-model="showQuitChildren"
        label="退園した児童を表示"
        hide-details
        color="primary"
      ></v-checkbox>
    </div>

    <div v-if="showDownloadMessage" class="my-4">
      <v-alert
        variant="tonal"
        type="success"
      >
        <p>成長記録のエクスポートを開始しました。</p>
        <p>
          <router-link to="/download/list">ダウンロードページ</router-link>
          からご確認ください。
        </p>
      </v-alert>
    </div>
    
    <div v-if="isFetching" class="my-2">
      <v-progress-linear indeterminate color="primary" />
    </div>

    <div>
      <v-expansion-panels>
        <v-expansion-panel
          v-for="c in classesWithRecords"
          :key="c.classObj.classId"
          class="my-1"
        >
          <v-expansion-panel-title>
            <div>{{ c.classObj.className }}</div>
            <v-spacer />
            <div class="d-flex justify-end align-center">
              <v-btn
                color="primary"
                rounded
                variant="outlined"
                class="mr-4"
                size="small"
                @click.stop="exportRecords(c.classObj)"
              >
                エクスポート
              </v-btn>
              <v-btn
                color="primary"
                rounded
                variant="flat"
                size="small"
                :to="`/growth-record/class/${c.classObj.classId}`"
                @click.stop
              >
                クラスの記録へ
              </v-btn>
            </div>
          </v-expansion-panel-title>
          
          <v-expansion-panel-text>
            <div>
              <v-table>
                <thead>
                  <tr>
                    <th class="text-left">児童氏名</th>
                    <th class="text-left">性別</th>
                    <th class="text-left">年齢</th>
                    <th class="text-left">退園日</th>
                    <th class="text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="record in c.records"
                    :key="record.child.childId"
                  >
                    <td>{{ record.child.lastName }} {{ record.child.firstName }}</td>
                    <td>{{ record.child.gender === 'male' ? '男性' : '女性' }}</td>
                    <td>{{ formatAge(record.child.dateOfBirth) }}</td>
                    <td>{{ record.child.quitDate }}</td>
                    <td>
                      <v-btn
                        color="primary"
                        rounded
                        variant="outlined"
                        size="small"
                        :to="`/growth-record/child/${record.child.childId}?year=${year}`"
                      >
                        児童の記録へ
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import PageTitle from '@/components/atoms/PageTitle.vue';

import { classModule } from '@/store/dataModules/classModule';
import { growthRecordsModule } from '@/store/viewModules/growth-record/growthRecordsModule';
import LocalDataService from '@/service/LocalDataService';
import { NurseryClassResponse, NurseryGrowthDetailResponse } from 'chaild-api/lib';
import dayjs from 'dayjs';
import ApiGrowthRecord from '@/api/ApiGrowthRecord';
import CommonUtil from '@/utils/CommonUtil';

// ⭕️ 修正8: タイポ修正 (ClassWithRecrods -> ClassWithRecords)
interface ClassWithRecords {
  classObj: NurseryClassResponse;
  records: NurseryGrowthDetailResponse[];
}

interface DataType {
  isFetching: boolean;
  showQuitChildren: boolean;
  showDownloadMessage: boolean; // タイポ修正
  year: number;
}

export default defineComponent({
  components: {
    PageTitle,
  },

  data: (): DataType => ({
    isFetching: false,
    showQuitChildren: false,
    showDownloadMessage: false,
    year: dayjs().year()
  }),

  computed: {
    childrenTableHeaders() {
      return [
        { text: '児童氏名', value: 'name' },
        { text: '性別', value: 'gender' },
        { text: '年齢', value: 'age' },
        { text: '-', value: 'actions' },
      ]
    },
    tableItems() {
      return [
        {
          name: 'テスト',
          gender: 'male',
          age: '1',
          actions: null,
        }
      ]
    },
    classes() {
      return classModule.classes;
    },
    records() {
      return growthRecordsModule.growthRecords;
    },
    classesWithRecords(): ClassWithRecords[] {
      if (!this.classes || !this.records) return [];

      return this.classes.map((c) => {
        const records = this.records.filter((r) => {
          const findClass = r.child?.nurseryClasses?.find((cl) => cl.classId === c.classId);
          if (findClass) {
            return true;
          }
          return false;
        })
        .filter((r) => {
          if (!this.showQuitChildren && r.child?.quitDate) {
            const now = dayjs();
            const quitDate = dayjs(r.child.quitDate);
            const diff = now.diff(quitDate);
            if (diff > 0) {
              return false;
            }
          }
          return true;
        });
        return {
          classObj: c,
          records: records,
        }
      });
    }
  },

  methods: {
    formatAge(dateOfBirth: string) {
      if (!dateOfBirth) return '';
      const now = dayjs();
      const age = now.diff(dayjs(dateOfBirth), 'year');
      return age;
    },
    async exportRecords(classObj: NurseryClassResponse) {
      const nurseryId = LocalDataService.getNurseryId()

      if (nurseryId && classObj.year) {
        this.isFetching = true;
        await ApiGrowthRecord.donwloadRecords({
          nurseryId,
          year: classObj.year,
          classId: classObj.classId
        }).catch(err => console.error(err));
        
        this.isFetching = false;
        this.showDownloadMessage = true;
      }
    }
  },

  watch: {
    year() {
      const nurseryId = LocalDataService.getNurseryId();

      if (nurseryId) {
        classModule.listClass({
          nurseryId: nurseryId,
          year: this.year
        })
        growthRecordsModule.listGrowthRecords(
          this.year
        )
      }
    }
  },

  async mounted() {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      this.year = parseInt(CommonUtil.getFiscalYear(dayjs().format('YYYY-MM')))
      this.isFetching = true;
      await Promise.all([
        classModule.listClass({
          nurseryId: nurseryId,
          year: this.year
        }),
        growthRecordsModule.listGrowthRecords(
          this.year
        ),
      ]);
      this.isFetching = false;
    }
  }
});
</script>